import { useMemo, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartResponsiveContainer } from "../../../components/ChartResponsiveContainer";
import { COLORS, CHART_PALETTE } from "../../../constants";
import { useDashboardOrigemPizzas } from "../../../hooks/useProducao";

const ORIGEM_COLORS: Record<string, string> = {
  cardapio: COLORS.PRIMARY,
  salao: COLORS.TEAL_DARK,
  balcao: COLORS.WARNING_DARK,
};

type ChartCardProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

const ChartCard = ({ title, subtitle, children }: ChartCardProps) => (
  <div className="col-span-1 w-full min-w-0 rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-5">
    <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
    {subtitle ? (
      <p className="mt-1 text-xs text-neutral-500">{subtitle}</p>
    ) : null}
    <div className="relative mt-4 h-[20rem] min-h-[20rem] w-full min-w-0 sm:h-[22rem] sm:min-h-[22rem]">
      {children}
    </div>
  </div>
);

type PieTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: { name?: string; value?: number; percentLabel?: string };
  }>;
};

const PieTooltip = ({ active, payload }: PieTooltipProps) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (p?.value == null) return null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{p.name}</p>
      <p className="mt-1 text-neutral-600">
        Pedidos: <span className="font-medium text-neutral-900">{p.value}</span>
        {p.percentLabel ? (
          <span className="text-neutral-500"> ({p.percentLabel})</span>
        ) : null}
      </p>
    </div>
  );
};

type BarTooltipProps = {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string }>;
  label?: string;
};

const BarTooltip = ({ active, payload, label }: BarTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{label}</p>
      <ul className="mt-1 space-y-0.5 text-neutral-600">
        {payload.map((item) => (
          <li key={item.name}>
            <span style={{ color: item.color }}>{item.name}</span>:{" "}
            <span className="font-medium text-neutral-900">{item.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

type DashboardOrigemChartsProps = {
  dataInicio: string;
  dataFim: string;
  lojaId: number | null;
};

export const DashboardOrigemCharts = ({
  dataInicio,
  dataFim,
  lojaId,
}: DashboardOrigemChartsProps) => {
  const { data, isLoading, isError } = useDashboardOrigemPizzas({
    dataInicio,
    dataFim,
    lojaId: lojaId || undefined,
  });

  const pieData = useMemo(() => {
    const total = data?.totalPedidos ?? 0;
    return (data?.pedidosPorOrigem ?? [])
      .filter((item) => item.quantidade > 0)
      .map((item) => ({
        key: item.origem,
        name: item.label,
        value: item.quantidade,
        percentLabel:
          total > 0
            ? `${((item.quantidade / total) * 100).toFixed(0)}%`
            : "0%",
      }));
  }, [data]);

  const barData = useMemo(
    () =>
      (data?.pizzasPorTamanhoOrigem ?? []).map((item) => ({
        name: item.tamanhoNome,
        Cardápio: item.cardapio,
        Salão: item.salao,
      })),
    [data]
  );

  if (isLoading) {
    return (
      <section className="space-y-3" aria-labelledby="dashboard-origem-pizzas">
        <h2
          id="dashboard-origem-pizzas"
          className="text-lg font-semibold text-neutral-800"
        >
          Origem dos pedidos e pizzas
        </h2>
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/80 py-16 text-center text-sm text-neutral-500">
          Carregando gráficos…
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="space-y-3" aria-labelledby="dashboard-origem-pizzas">
        <h2
          id="dashboard-origem-pizzas"
          className="text-lg font-semibold text-neutral-800"
        >
          Origem dos pedidos e pizzas
        </h2>
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/80 py-16 text-center text-sm text-neutral-500">
          Não foi possível carregar os gráficos deste período.
        </div>
      </section>
    );
  }

  const hasPie = pieData.length > 0;
  const hasBar = barData.length > 0;

  if (!hasPie && !hasBar) {
    return (
      <section className="space-y-3" aria-labelledby="dashboard-origem-pizzas">
        <h2
          id="dashboard-origem-pizzas"
          className="text-lg font-semibold text-neutral-800"
        >
          Origem dos pedidos e pizzas
        </h2>
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/80 py-16 text-center text-sm text-neutral-500">
          Nenhum pedido ou pizza montada no período selecionado.
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3" aria-labelledby="dashboard-origem-pizzas">
      <div>
        <h2
          id="dashboard-origem-pizzas"
          className="text-lg font-semibold text-neutral-800"
        >
          Origem dos pedidos e pizzas
        </h2>
        <p className="mt-1 text-sm text-neutral-600">
          Cardápio Web versus Foodfly (salão e balcão). Total:{" "}
          <span className="font-medium text-neutral-800">
            {data?.totalPedidos ?? 0} pedidos
          </span>
          {" · "}
          <span className="font-medium text-neutral-800">
            {data?.totalPizzas ?? 0} pizzas
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Pedidos por origem"
          subtitle="Cardápio Web, salão (mesa/comanda) e balcão Foodfly."
        >
          {hasPie ? (
            <ChartResponsiveContainer>
              <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="46%"
                  innerRadius="38%"
                  outerRadius="70%"
                  paddingAngle={1.5}
                  labelLine={false}
                  label={({ percent }) =>
                    (percent ?? 0) >= 0.06
                      ? `${((percent ?? 0) * 100).toFixed(0)}%`
                      : ""
                  }
                >
                  {pieData.map((item, index) => (
                    <Cell
                      key={item.key}
                      fill={
                        ORIGEM_COLORS[item.key] ??
                        CHART_PALETTE[index % CHART_PALETTE.length]
                      }
                      stroke={COLORS.WHITE}
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  iconSize={10}
                  wrapperStyle={{ fontSize: "11px", paddingTop: 8 }}
                />
              </PieChart>
            </ChartResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              Sem pedidos no período.
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Pizzas montadas por tamanho"
          subtitle="Comparativo Cardápio Web × Salão/Foodfly no mesmo tamanho."
        >
          {hasBar ? (
            <ChartResponsiveContainer>
              <BarChart
                data={barData}
                margin={{ top: 8, right: 8, left: 0, bottom: 32 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.NEUTRAL_200}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={48}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<BarTooltip />} />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
                <Bar
                  dataKey="Cardápio"
                  fill={ORIGEM_COLORS.cardapio}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="Salão"
                  fill={ORIGEM_COLORS.salao}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              Sem pizzas montadas no período.
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
};
